import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';

import { Text } from '@ui-component-library';

import { AlertDialog } from '@components/PropertySelector/components/AlertModal';

import type {
  AlertDialogContent,
  PaymentErrorResult,
  PaymentFetchResponse
} from '../Checkout.types';
import KlarnaCheckout from '../components/KlarnaCheckout';
import { useCheckout } from '../context/CheckoutContext';

function applyPaymentErrorResult(
  response: PaymentFetchResponse,
  handlers: {
    handlePaymentError?: (response: PaymentFetchResponse) => PaymentErrorResult | void;
    previousStep: () => void;
    setAlertQueue: Dispatch<SetStateAction<AlertDialogContent[]>>;
    setError: (msg: string | null) => void;
    navigateBackAfterAlertsRef: RefObject<boolean>;
  }
) {
  const result = handlers.handlePaymentError?.(response);

  if (result) {
    if (result.alerts?.length) {
      handlers.navigateBackAfterAlertsRef.current = Boolean(result.previousStep);
      handlers.setAlertQueue(result.alerts);
      return;
    }
    if (result.previousStep) {
      handlers.previousStep();
    }
    if (result.genericError !== false) {
      handlers.setError(response.message ?? 'Unknown error');
    }
    return;
  }

  handlers.setError(response.message ?? 'Unknown error');

  console.error('Payment fetch error:', response.message ?? response.data?.error);
}

export const PaymentStep = () => {
  const {
    isCheckoutOpen,
    selectedProducts,
    couponDiscount,
    extraData,
    nextStep,
    previousStep,
    setPaymentMethod,
    setStartedAsAnonymous,
    config
  } = useCheckout();
  const [klarnaHtml, setKlarnaHtml] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertQueue, setAlertQueue] = useState<AlertDialogContent[]>([]);
  const navigateBackAfterAlertsRef = useRef(false);

  useEffect(() => {
    const initPayment = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await config.fetchCheckout?.(selectedProducts, couponDiscount, extraData);
        if (!response) {
          setError('No checkout fetch function provided');
          return;
        }
        if (response.ok) {
          if (response.data?.klarna_html) {
            setPaymentMethod('klarna');
            setKlarnaHtml(response.data.klarna_html);
            config.onKlarnaReady?.();
          } else {
            setPaymentMethod('invoice');
            nextStep();
            config.onInvoiceFallback?.();
          }
          setStartedAsAnonymous(false);
        } else {
          applyPaymentErrorResult(response, {
            handlePaymentError: config.handlePaymentError,
            previousStep,
            setAlertQueue,
            setError,
            navigateBackAfterAlertsRef
          });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'A network error occurred';
        applyPaymentErrorResult(
          { ok: false, message: errorMsg, data: { error: 'Network error', message: errorMsg } },
          {
            handlePaymentError: config.handlePaymentError,
            previousStep,
            setAlertQueue,
            setError,
            navigateBackAfterAlertsRef
          }
        );
        console.error('Unhandled initPayment error:', err);
      } finally {
        setLoading(false);
      }
    };

    initPayment();
  }, []);

  if (!isCheckoutOpen) {
    return null;
  }

  const currentAlert = alertQueue[0];

  return (
    <>
      <div className="flex min-h-0 min-h-[400px] flex-1 flex-col overflow-y-auto">
        <div className="flex min-h-0 flex-1">
          {klarnaHtml && <KlarnaCheckout htmlMarkup={klarnaHtml} />}
          {error && (
            <div className="flex flex-1 items-center justify-center">
              <Text variant="text-base/leading-normal/medium">
                Oops! Något gick fel. Ring 08-33 99 44 så hjälper vi dig vidare.
              </Text>
            </div>
          )}
          {loading && (
            <div className="flex flex-1 items-center justify-center">
              <Text variant="text-base/leading-normal/medium">Hämtar betalningslösning...</Text>
            </div>
          )}
        </div>
      </div>
      {currentAlert &&
        createPortal(
          <AlertDialog
            {...currentAlert}
            isOpen
            onClose={() => {
              currentAlert.onClose?.();
              setAlertQueue((queue) => {
                const next = queue.slice(1);
                if (next.length === 0 && navigateBackAfterAlertsRef.current) {
                  navigateBackAfterAlertsRef.current = false;
                  queueMicrotask(() => {
                    previousStep();
                  });
                }
                return next;
              });
            }}
          />,
          document.body
        )}
    </>
  );
};
